"use client"

import React from "react"
import { motion, useReducedMotion } from "motion/react"
import type { Media, Service } from "@/payload-types"
import Link from "next/link"
import Image from "next/image"

export interface ServicesGridBlock {
	title: string
	subtitle?: string | null
	filter: {
		mode: "featured" | "category" | "manual" | "all"
		category?: string | Record<string, unknown> | null
		services?: (string | Service)[] | null
		limit?: number | null
	}
	display: "grid-3" | "grid-2" | "list"
	showDescription?: boolean | null
	ctaText?: string | null
	ctaLink?: string | null
	blockType: "servicesGrid"
}

interface AnimatedGridProps {
	services: Service[]
	display: ServicesGridBlock["display"]
	showDescription?: boolean
	ctaText?: string | null
	ctaLink?: string | null
}

const containerVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.1,
		},
	},
}

const itemVariants = {
	hidden: { opacity: 0, y: 20 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.5,
		},
	},
}

export const AnimatedGrid: React.FC<AnimatedGridProps> = ({
	services,
	display = "grid-4",
	showDescription = true,
	ctaText,
	ctaLink,
}) => {
	const shouldReduceMotion = useReducedMotion()
	const gridClasses = {
		"grid-4": "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
		"grid-2": "grid-cols-1 md:grid-cols-2",
		list: "grid-cols-1",
	}[display]

	if (!services || services.length === 0) {
		return null
	}

	return (
		<div className="py-12">
			<motion.div
				variants={containerVariants}
				initial={shouldReduceMotion ? "visible" : "hidden"}
				whileInView="visible"
				viewport={{ once: true, margin: "-100px" }}
				className={`grid gap-8 ${gridClasses}`}
			>
				{services.map((service) => (
					<motion.div
						key={service.id}
						variants={itemVariants}
						className={`group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white transition-all hover:border-neutral-300 hover:shadow-xl dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-neutral-700 ${
							display === "list"
								? "flex flex-col md:flex-row md:items-center"
								: ""
						}`}
					>
						{/* Media/Icon Section */}
						{display === "list" ? (
							// List view: image/icon on the left
							<div className="flex items-stretch">
								{service.gallery && service.gallery.length > 0 ? (
									// Image in list view: fixed width on left
									<div className="relative h-48 w-64 flex-shrink-0 overflow-hidden">
										<Image
											src={
												typeof service.gallery[0].image === "string"
													? service.gallery[0].image
													: (service.gallery[0].image as Media).url ||
														"/placeholder.jpg"
											}
											alt={service.title}
											fill
											className="object-cover transition-transform duration-300 group-hover:scale-105"
											sizes="(max-width: 768px) 100vw, 256px"
										/>
									</div>
								) : (
									// Icon in list view
									<div className="flex h-48 w-24 items-center justify-center bg-neutral-100 dark:bg-neutral-800">
										<span className="text-4xl" role="img" aria-label="icon">
											{service.icon || "🛠️"}
										</span>
									</div>
								)}
							</div>
						) : // Grid view: image/icon at the top
						service.gallery && service.gallery.length > 0 ? (
							// Image in grid view
							<div className="relative aspect-video w-full overflow-hidden">
								<Image
									src={
										typeof service.gallery[0].image === "string"
											? service.gallery[0].image
											: (service.gallery[0].image as Media).url ||
												"/placeholder.jpg"
									}
									alt={service.title}
									fill
									className="object-cover transition-transform duration-300 group-hover:scale-105"
									sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
								/>
							</div>
						) : (
							// Icon in grid view
							<div className="flex h-48 w-full items-center justify-center bg-neutral-100 dark:bg-neutral-800">
								<span className="text-6xl" role="img" aria-label="icon">
									{service.icon || "🛠️"}
								</span>
							</div>
						)}

						{/* Content Section */}
						<div className="flex flex-1 flex-col p-8">
							<div className="flex-1">
								<h3 className="mb-3 text-xl font-bold text-neutral-900 dark:text-white">
									{service.title}
								</h3>

								{showDescription && (
									<p className="mb-6 text-neutral-700 dark:text-neutral-300">
										{service.description}
									</p>
								)}

								<div className="flex flex-wrap gap-4 text-sm font-medium text-neutral-600 dark:text-neutral-400">
									{service.duration && (
										<div className="flex items-center gap-1.5">
											<span>⏱️</span>
											<span>{service.duration}</span>
										</div>
									)}
									{service.warranty && (
										<div className="flex items-center gap-1.5">
											<span>🛡️</span>
											<span>{service.warranty}</span>
										</div>
									)}
								</div>
							</div>

							<div className="mt-8">
								<Link
									href={`/servicios/${service.slug}`}
									className="inline-flex items-center gap-2 font-semibold text-neutral-900 transition-colors hover:text-neutral-600 dark:text-white dark:hover:text-neutral-300"
								>
									Ver detalles
									<svg
										className="h-4 w-4 transition-transform group-hover:translate-x-1"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
										aria-hidden="true"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M17 8l4 4m0 0l-4 4m4-4H3"
										/>
									</svg>
								</Link>
							</div>
						</div>
					</motion.div>
				))}
			</motion.div>

			{ctaText && ctaLink && (
				<div className="mt-16 text-center">
					<Link
						href={ctaLink}
						className="inline-flex h-12 items-center justify-center rounded-full bg-neutral-900 px-8 text-sm font-bold text-white transition-all hover:bg-neutral-800 hover:shadow-lg dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-200"
					>
						{ctaText}
					</Link>
				</div>
			)}
		</div>
	)
}
